import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FAQS = [
  {
    q: "What does POG stand for?",
    a: "POG stands for Passion fruit, Orange, and Guava — a tropical juice blend invented at Meadow Gold in Hawai‘i in 1971 and now one of the most recognized regional flavors in the Pacific.",
  },
  {
    q: "What is POG Vodka?",
    a: "POG Vodka is a 750 ml premium vodka at 35% alcohol by volume (70 proof), flavored with real passion fruit, orange, and guava. It launches in 2026 as part of POG Spirits — an independent spirits brand created in partnership with Meadow Gold.",
  },
  {
    q: "What is POG Spiked Island Punch?",
    a: "POG Spiked Island Punch is a ready-to-drink sparkling flavored beverage in an 11.5 fl oz (340 ml) can at 6% ABV. It contains real passion fruit juice, guava purée, orange juice, sparkling water, and organic cane sugar — 160 calories per can.",
  },
  {
    q: "Where is POG Spirits made?",
    a: "POG Spirits is made in Hawai‘i. The Spiked Island Punch RTD is produced by Hawaiian Ola Brewing Corporation in Kailua-Kona, HI. POG Vodka is bottled by NLV Bottling in North Las Vegas, NV.",
  },
  {
    q: "When does POG Spirits launch and where can I buy it?",
    a: "POG Spirits launches in 2026, first across Hawai‘i (O‘ahu, Maui, Big Island, Kaua‘i), distributed by Reyes and Island Distributing across major grocers, retail chains, resorts, and premium bars — then rolling out to California and Nevada.",
  },
  {
    q: "How is POG Spirits related to POG juice?",
    a: "POG juice was invented at Meadow Gold in Hawai‘i in 1971. POG Spirits is an independent spirits brand launching in 2026, created in partnership with Meadow Gold and inspired by that original flavor — now bottled with real passion fruit, orange, and guava.",
  },
  {
    q: "What does POG Vodka taste like?",
    a: "Passion fruit blossom and ripe orange peel on the aroma; tropical sunlight, clean grain, and a whisper of sea salt on the palate; a crisp, dry, long warm finish.",
  },
];

const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-32 md:py-48 bg-[#14100D] text-[#EFE7D6] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="grid md:grid-cols-12 gap-14 md:gap-20"
        >
          <div className="md:col-span-4">
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">— FAQ</span>
              <span className="w-16 hairline-strong h-px inline-block" />
            </div>
            <h2 className="display-tight text-4xl md:text-6xl">
              Ask us
              <br />
              <span className="italic text-[#DCCFB4]">anything.</span>
            </h2>
            <p className="font-body text-[#DCCFB4]/80 mt-6 leading-[1.7] font-light max-w-md">
              The essentials on POG, the brand, the launch, and where to find it.
            </p>
          </div>

          <div className="md:col-span-8">
            <div className="border-t border-[#EFE7D6]/12">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={f.q}
                    className="border-b border-[#EFE7D6]/12"
                    itemScope
                    itemType="https://schema.org/Question"
                    data-testid={`faq-item-${i}`}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      className="w-full flex items-baseline justify-between gap-6 py-6 md:py-8 text-left group"
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${i}`}
                      data-testid={`faq-toggle-${i}`}
                    >
                      <h3
                        itemProp="name"
                        className="font-display text-xl md:text-2xl text-[#EFE7D6] group-hover:text-[#E85A15] transition-colors tracking-tight"
                      >
                        {f.q}
                      </h3>
                      <span
                        className={`shrink-0 counter transition-transform duration-500 ${
                          isOpen ? "rotate-45 text-[#E85A15]" : ""
                        }`}
                        aria-hidden="true"
                      >
                        +
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="answer"
                          id={`faq-panel-${i}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                          itemScope
                          itemProp="acceptedAnswer"
                          itemType="https://schema.org/Answer"
                        >
                          <p
                            itemProp="text"
                            className="font-body text-base md:text-lg text-[#DCCFB4]/85 leading-[1.75] font-light pb-8 md:pb-10 max-w-2xl"
                          >
                            {f.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
